import Icon from '@/components/ui/AppIcon';

interface Device {
  name: string;
  icon: string;
  requirements: string[];
  supported: boolean;
}

const DeviceCompatibility = () => {
  const devices: Device[] = [
    {
      name: "iPhone",
      icon: "DevicePhoneMobileIcon",
      requirements: ["iOS 14.0 or later", "iPhone 8 and newer", "64-bit processor"],
      supported: true
    },
    {
      name: "iPad",
      icon: "DeviceTabletIcon",
      requirements: ["iPadOS 14.0 or later", "All iPad Pro models", "iPad Air 3rd gen and newer"],
      supported: true
    },
    {
      name: "Android Phone",
      icon: "DevicePhoneMobileIcon",
      requirements: ["Android 8.0 or later", "2GB RAM minimum", "ARM64 processor"],
      supported: true
    },
    {
      name: "Android Tablet",
      icon: "DeviceTabletIcon",
      requirements: ["Android 8.0 or later", "Optimized for 7-12 inch screens", "3GB RAM recommended"],
      supported: true
    }
  ];

  const browsers = [
    { name: "Chrome", version: "90+", icon: "GlobeAltIcon" },
    { name: "Safari", version: "14+", icon: "GlobeAltIcon" },
    { name: "Firefox", version: "88+", icon: "GlobeAltIcon" },
    { name: "Edge", version: "90+", icon: "GlobeAltIcon" }
  ];

  return (
    <section className="py-20 px-4 lg:px-8 bg-card">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Device Compatibility
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Signaly works seamlessly across all your devices with optimized performance
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {devices.map((device, index) => (
            <div key={index} className="bg-background border border-border rounded-2xl p-6 hover:shadow-cyan-glow transition-all duration-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-full">
                  <Icon name={device.icon as any} size={24} className="text-primary" />
                </div>
                {device.supported && (
                  <div className="flex items-center space-x-1 px-3 py-1 bg-success/10 rounded-full">
                    <Icon name="CheckCircleIcon" size={16} className="text-success" />
                    <span className="text-xs font-medium text-success">Supported</span>
                  </div>
                )}
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground mb-3">{device.name}</h3>
              <ul className="space-y-2">
                {device.requirements.map((req, reqIndex) => (
                  <li key={reqIndex} className="flex items-start space-x-2 text-sm text-muted-foreground">
                    <Icon name="CheckIcon" size={16} className="text-primary mt-0.5 flex-shrink-0" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-background border border-border rounded-2xl p-8">
          <h3 className="text-2xl font-heading font-bold text-foreground mb-6 text-center">
            Browser Support
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {browsers.map((browser, index) => (
              <div key={index} className="flex items-center space-x-3 p-4 bg-card rounded-xl">
                <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-full">
                  <Icon name={browser.icon as any} size={20} className="text-primary" />
                </div>
                <div>
                  <div className="text-foreground font-medium">{browser.name}</div>
                  <div className="text-sm text-muted-foreground">Version {browser.version}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground text-center mt-6">
            Progressive enhancement ensures basic functionality on older browser versions
          </p>
        </div>
      </div>
    </section>
  );
};

export default DeviceCompatibility;